// src/pages/Advisor/AOBFStep3.jsx
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import ACSnackbar from '../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import RatingCard from './RatingCard';
import { submitAOBFStep3 } from '../../services/advisor_services';

const useStyles = (isSM) => ({
  main: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '16px 120px 0',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
    width: '100%',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: { xxs: '32px', xs: '24px' },
    letterSpacing: '-0.43px',
    marginBottom: '16px',
    padding: { xxs: '0 8px', xs: '0' },
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    color: '#646D7A',
    maxWidth: '500px',
    textAlign: 'center',
    padding: { xxs: '0 8px', xs: '0' },
    mb: 2,
  },
  note: {
    fontSize: '12px',
    color: '#646D7A',
    lineHeight: '20px',
    textAlign: 'center',
    maxWidth: '520px',
    mb: 2,
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  section: {
    width: '100%',
    mb: 2,
  },
  chipBase: {
    borderRadius: '999px',
    border: '1px solid #C7D0DE',
    mr: 1,
    mb: 1,
  },
  chipSelected: {
    backgroundColor: '#3171F6',
    color: '#fff',
    '& .MuiChip-label': { color: '#fff', fontWeight: 600 },
    '&:hover': { backgroundColor: '#3171F6' },
  },
  yesNoContainer: {
    display: 'flex',
    gap: 1.5,
  },
  yesNoBtn: (active) => ({
    flex: 1,
    borderRadius: '12px',
    border: '1px solid #3171F6',
    backgroundColor: active ? '#3171F6' : '#F5F7FA',
    color: active ? '#fff' : '#3171F6',
    textTransform: 'none',
    fontWeight: 700,
    height: 44,
    '&:hover': {
      backgroundColor: active ? '#3171F6' : '#F5F7FA',
    },
  }),
});

const BOARD_PRIORITY_OPTIONS = [
  'Emerging Startups',
  'Small Business',
  'Private Companies',
  'International Business',
  'Non-Profits',
  'Corporate Teams',
];

const stripPerkEmoji = (value = '') => value.replace(/^[^\w\s]+ /, '');

const PERKS_OPTIONS = [
  '📝 Add Board Roles to Resume',
  '📈 Equity Potential',
  '💰 Compensation Potential',
  '💼 Fractional or Full-Time Roles',
  '🎁 Product or Service Discounts',
];

const PERKS_OPTIONS_BY_LABEL = PERKS_OPTIONS.reduce((acc, option) => {
  acc[stripPerkEmoji(option)] = option;
  return acc;
}, {});

const normalizePerkSelections = (perks) => {
  if (!Array.isArray(perks)) {
    return [];
  }

  return perks.map((perk) => {
    const key = stripPerkEmoji(perk);
    return PERKS_OPTIONS_BY_LABEL[key] ?? perk;
  });
};

export default function AOBFStep3() {
  const { accountDetails, currentPlan } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const history = useHistory();

  // Prefill desde el perfil si ya existe algo almacenado
  const [boardMatchPriority, setBoardMatchPriority] = useState(
    Array.isArray(accountDetails?.boardMatchPriority)
      ? accountDetails.boardMatchPriority
      : []
  );
  const [boardMatchPreferredPerks, setBoardMatchPreferredPerks] = useState(() =>
    normalizePerkSelections(accountDetails?.boardMatchPreferredPerks)
  );

  useEffect(() => {
    const normalizedPerks = normalizePerkSelections(
      accountDetails?.boardMatchPreferredPerks
    );

    setBoardMatchPreferredPerks((currentPerks) => {
      if (
        normalizedPerks.length === currentPerks.length &&
        normalizedPerks.every((perk, index) => perk === currentPerks[index])
      ) {
        return currentPerks;
      }

      return normalizedPerks;
    });
  }, [accountDetails?.boardMatchPreferredPerks]);

  console.log('boardMatchPreferredPerks', boardMatchPreferredPerks);
  const [companyAccountInterest, setCompanyAccountInterest] = useState(
    accountDetails?.companyAccountInterest || ''
  );

  const [submitting, setSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const disableButton = useMemo(() => {
    return (
      submitting || boardMatchPriority.length === 0 || !companyAccountInterest
    );
  }, [boardMatchPriority, companyAccountInterest, submitting]);

  const togglePerk = (perk) => {
    setBoardMatchPreferredPerks((prev) =>
      prev.includes(perk) ? prev.filter((p) => p !== perk) : [...prev, perk]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await submitAOBFStep3({
        id: accountDetails.id,
        boardMatchPriority, // array<string>
        boardMatchPreferredPerks: boardMatchPreferredPerks.map((perk) =>
          stripPerkEmoji(perk)
        ), // array<string> sin emojis
        companyAccountInterest, // 'Yes' | 'No'
      });
      history.push('/advisor/aobf/step4');
    } catch (err) {
      console.error(err);
      setOpenSnackbar(true);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={isSM ? 240 : 180}
            height={isSM ? 80 : 60}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <Typography sx={classes.title}>
            Get Matched to Advisory Boards
          </Typography>
          <Typography sx={classes.subtitle}>
            AdvisoryCloud builds boards for businesses of all sizes, so we can
            guarantee board placement on our platform. The days of the 5-person
            board are over! Businesses want bigger boards and new perspectives.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ p: 2, width: '100%' }}
          >
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps} md={10}>
                {/* 2) Multi-select picklist: boardMatchPriority */}
                <Box sx={classes.section}>
                  <Typography sx={classes.label}>
                    What types of boards are you most excited to join?
                    <span style={{ fontWeight: 400 }}>
                      {' '}
                      (Select all that apply)
                    </span>
                  </Typography>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={BOARD_PRIORITY_OPTIONS}
                    value={boardMatchPriority}
                    onChange={(_, value) => setBoardMatchPriority(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select all that apply"
                        inputProps={{
                          ...params.inputProps,
                          readOnly: true,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            '& fieldset': { borderColor: '#C7D0DE' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box sx={classes.note}>
                  While advisory board roles are not paid by default, board
                  members receive various perks &amp; benefits, plus more
                  visibility to be hired for paid advisory work.
                </Box>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid xs={12} sm={12} md={6}>
                <Box sx={classes.section}>
                  <Typography sx={{ ...classes.label, textAlign: 'center' }}>
                    Which potential perks &amp; benefits interest you?
                  </Typography>
                  <Typography
                    sx={{
                      ...classes.label,
                      fontWeight: 400,
                      textAlign: 'center',
                    }}
                  >
                    (Check all that apply)
                  </Typography>
                  {PERKS_OPTIONS.map((perk, index) => {
                    const selected = boardMatchPreferredPerks.includes(perk);
                    return (
                      <Grid item xs={12} sm={12} md={12} key={perk}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => togglePerk(perk)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '16px',
                            borderRadius: '16px',
                            margin: '8px 0px',
                            padding: '6px 12px',
                            width: '100%',
                            backgroundColor: selected ? '#2B71F6' : '#F5F7FA',
                            color: selected ? '#FFFFFF' : '#232B35',
                            borderColor: selected ? '#2B71F6' : '#F5F7FA',
                            '&:hover': {
                              backgroundColor: selected ? '#2B71F6' : '#F5F7FA',
                              color: selected ? '#FFFFFF' : '#232B35',
                              borderColor: selected ? '#2B71F6' : '#F5F7FA',
                            },
                          }}
                        >
                          {perk}
                        </Button>
                      </Grid>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps} md={8}>
                <Box
                  sx={{
                    ...classes.section,
                    mt: 1,
                    backgroundColor: '#F5F7FA',
                    py: 3,
                    px: 2,
                    borderRadius: '8px',
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      textAlign: 'center',
                      color: '#001030',
                      fontWeight: 600,
                      fontSize: '14px',
                      mb: 2,
                    }}
                  >
                    Would you ever consider starting an advisory board for your
                    own business, team, or project?
                  </Typography>
                  <Box sx={classes.yesNoContainer}>
                    <Button
                      type="button"
                      onClick={() => setCompanyAccountInterest('Yes')}
                      sx={classes.yesNoBtn(companyAccountInterest === 'Yes')}
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setCompanyAccountInterest('No')}
                      sx={classes.yesNoBtn(companyAccountInterest === 'No')}
                    >
                      No
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps} md={8}>
                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={disableButton}
                    id="aobf-step4-submit-button"
                    sx={{
                      borderRadius: '50px',
                      backgroundColor: '#3171F6',
                      '&:hover': { backgroundColor: '#3171F6' },
                      height: '48px',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontWeight: 700,
                      mb: '16px',
                    }}
                  >
                    {submitting ? <LoadingLogoSkeleton /> : 'Generate Matches'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <RatingCard
                  quote="I have been successfully matched by AdvisoryCloud with two companies that would never have been on my radar."
                  name="Michael Harris"
                  title="CEO, Michael Harris Group"
                />
              </Grid>
            </Grid>

            <ACSnackbar
              onClose={() => setOpenSnackbar(false)}
              open={openSnackbar}
              severity="error"
              text="Something went wrong"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}
